<template>
  <div v-show="show" :class="$options.name" v-bind="attrs.binds">
    <slot />
  </div>
</template>
<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, GoogleMap, LatLng, wrapComponentElementForGoogle, type InfoWindowEventMap, type InfoWindowOptions, type InfoWindowOverlay } from 'tg-map-core'
import { defineComponent } from 'vue'
import { TgMap } from '..'
import { eventProp, findAncestorComponentByType, optionalProp, requiredProp, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import MapMixin from '../map-mixin'
import TgMarker, { type TgMarkerPublicInstance } from './TgMarker.vue'

type TgInfoWindowEmits = {
  'update:show': boolean
}
const TgInfoWindow = defineComponent({
  name: 'tg-info-window',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: {
    show: requiredProp(Boolean),
    position: optionalProp(LatLng),
    maxWidth: optionalProp(Number),
    offset: optionalProp(Object),
    disableAutoPan: optionalProp(Boolean),
    'onUpdate:show': eventProp(),
  } satisfies Props<Omit<InfoWindowOptions, 'content'> & { show: boolean }> & EventProps<TgInfoWindowEmits>,
  emits: undefined as any as EventEmits<InfoWindowEventMap & TgInfoWindowEmits>,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      overlay: lateinit<InfoWindowOverlay>(),
    }
  },
  watch: {
    show(isShow: boolean) {
      if (this.$marker()) {
        this.$marker()!.onInfoShowChanged(this, isShow)
      } else {
        isShow ? this.overlay.open() : this.overlay.close()
      }
    },
    position(value) {
      this.overlay.setPosition(value)
    },
    maxWidth() {
      this.recreate()
    },
    offset() {
      this.recreate()
    },
    disableAutoPan() {
      this.recreate()
    },
  },
  methods: {
    content(): HTMLElement {
      if (this.$map instanceof GoogleMap) {
        return wrapComponentElementForGoogle(this.$el, 'google-info-container')
      }
      return this.$el as HTMLElement
    },
    $marker(): TgMarkerPublicInstance | undefined {
      return findAncestorComponentByType(TgMarker, this.$parent, TgMap)
    },
    getOptions(): InfoWindowOptions {
      return {
        content: this.content(),
        position: this.position,
        maxWidth: this.maxWidth,
        offset: this.offset,
        disableAutoPan: this.disableAutoPan,
      }
    },
  },
  onCreate() {
    this.overlay = this.$map.createInfoWindow(this.getOptions())
    if (this.$marker()) {
      this.$marker()!.onAddInfo(this)
    } else {
      this.show && this.overlay.open()
    }

    // 监听open/close
    this.overlay.addEventListener('open', () => {
      this.$emit('update:show', true)
    })
    this.overlay.addEventListener('close', () => {
      this.$emit('update:show', false)
    })

    bindEvents(this.attrs.listeners, this.overlay, this.$emit)
  },
  onDestroy() {
    if (this.$marker()) {
      this.$marker()!.onRemoveInfo(this)
    } else {
      this.overlay.close()
    }
  },
})
type TgInfoWindow = Omit<InstanceType<typeof TgInfoWindow>, '$options'>
export default TgInfoWindow
</script>
